import { ChangeEvent, ComponentProps, FC, ReactNode } from 'react'

import { FormMetadata, getFormProps } from '@conform-to/react'
import { Form } from '@remix-run/react'
import { z } from 'zod'

import { MultiSelect } from '~/components'
import {
  AreasMultiSelectForm,
  areasMultiSelectFormSchema,
  CheckBoxOptions,
  FacilitiesAndServicesSection,
  facilitiesSchema,
  keywordsSchema,
  KeywordsSection,
  monthlyCostsFormSchema,
  MonthlyCostsSection,
  NumberOfUsersMultiSelectForm,
  numberOfUsersMultiSelectFormSchema,
  OfficeTypeMultiSelectForm,
  officeTypeMultiSelectFormSchema,
  SearchFormSectionLayout,
} from '~/features/common'

export { FormProvider } from '@conform-to/react'
export const searchFormSchema = z
  .object({})
  .merge(keywordsSchema)
  .merge(areasMultiSelectFormSchema)
  .merge(officeTypeMultiSelectFormSchema)
  .merge(numberOfUsersMultiSelectFormSchema)
  .merge(monthlyCostsFormSchema)
  .merge(facilitiesSchema)

export type SearchFormType = z.infer<typeof searchFormSchema>

type Options = React.ComponentProps<typeof MultiSelect>['options']
export type FormValues = {
  areas?: string[]
  officeTypes?: string[]
  facilities?: string[]
  minPeople?: string
  minMonthlyRent?: string
  maxMonthlyRent?: string
  keyword?: string
}

export type FormType = FormMetadata<SearchFormType>
export type FieldType = ReturnType<FormType['getFieldset']>
export type SearchFormContext = {
  areaFormOptions: Options
  officeTypeFormOptions: Options
  facilitiesAndServicesFormOptions: CheckBoxOptions
  onSubmit?: () => void
  form: FormType
  fields: FieldType
  prefectureSlug: string
  watchedFormValues: FormValues
  areasValue: string[]
  areasInputName: string
  onChangeAreas: (value: string[]) => void
  officeTypesValue: string[]
  officeTypeInputName: string
  onChangeOfficeTypes: (value: string[]) => void
  minPeopleValue: string
  minPeopleInputName: string
  onChangeMinPeople: ComponentProps<
    typeof NumberOfUsersMultiSelectForm
  >['onChange']
  minMonthlyRentValue: string
  maxMonthlyRentValue: string
  onChangeMinMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  onChangeMaxMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  minMonthlyRentName: string
  maxMonthlyRentName: string
  onBlurMinMonthlyRent: () => void
  onBlurMaxMonthlyRent: () => void
  facilitiesValue: string[]
  onChangeFacilities: (e: ChangeEvent<HTMLInputElement>) => void
  facilitiesInputName: string
  keywordValue: string
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void
  keywordInputName: string
  onEnterKeyword: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlurKeyword: () => void
}

type SearchFormProps = {
  children: ReactNode
} & SearchFormContext

// HACK: レイアウトで都道府県TOPで使用しているSearchFormSectionLayoutを使っているが、クラスを当てて無理やり使っているので適切なコンポーネントを作成する
export const SearchForm: FC<SearchFormProps> = ({
  children,
  ...searchFormContext
}) => {
  const {
    form,
    fields,
    prefectureSlug,
    areaFormOptions,
    officeTypeFormOptions,
    facilitiesAndServicesFormOptions,
    areasValue,
    onChangeAreas,
    areasInputName,
    officeTypesValue,
    onChangeOfficeTypes,
    officeTypeInputName,
    minPeopleValue,
    minPeopleInputName,
    onChangeMinPeople,
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
    facilitiesValue,
    onChangeFacilities,
    facilitiesInputName,
    keywordValue,
    onChangeKeyword,
    keywordInputName,
    onEnterKeyword,
    onBlurKeyword,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
  } = searchFormContext

  return (
    <Form method="post" {...getFormProps(form)}>
      <input name="prefectureSlug" type="hidden" value={prefectureSlug} />
      <SearchFormSectionLayout
        headerClassName="md:w-full md:border-t-0"
        layoutClassName="flex flex-col md:border-none"
        title="エリア"
      >
        <div className="grow p-4">
          <AreasMultiSelectForm
            name={areasInputName}
            onChange={onChangeAreas}
            options={areaFormOptions}
            value={areasValue}
          />
        </div>
      </SearchFormSectionLayout>
      <SearchFormSectionLayout
        headerClassName="md:w-full"
        layoutClassName="flex flex-col md:border-none"
        title="オフィスタイプ"
      >
        <div className="grow p-4">
          <OfficeTypeMultiSelectForm
            name={officeTypeInputName}
            onChange={onChangeOfficeTypes}
            options={officeTypeFormOptions}
            value={officeTypesValue}
          />
        </div>
      </SearchFormSectionLayout>
      <SearchFormSectionLayout
        headerClassName="md:w-full"
        layoutClassName="flex flex-col md:border-none"
        title="最大人数"
      >
        <div className="grow p-4">
          <NumberOfUsersMultiSelectForm
            name={minPeopleInputName}
            onChange={onChangeMinPeople}
            value={minPeopleValue}
          />
        </div>
      </SearchFormSectionLayout>
      <MonthlyCostsSection
        headerClassName="md:w-full"
        layoutClassName="flex flex-col md:border-none"
        maxMonthlyRentName={maxMonthlyRentName}
        maxMonthlyRentValue={maxMonthlyRentValue}
        maxValueErrorMessages={fields.maxMonthlyRent.errors}
        minMonthlyRentName={minMonthlyRentName}
        minMonthlyRentValue={minMonthlyRentValue}
        minValueErrorMessages={fields.minMonthlyRent.errors}
        onBlurMaxMonthlyRent={onBlurMaxMonthlyRent}
        onBlurMinMonthlyRent={onBlurMinMonthlyRent}
        onChangeMaxMonthlyRent={onChangeMaxMonthlyRent}
        onChangeMinMonthlyRent={onChangeMinMonthlyRent}
      />
      <FacilitiesAndServicesSection
        columnCount={{ pc: 2 }}
        headerClassName="md:w-full"
        layoutClassName="flex flex-col md:border-none"
        meta={fields.facilities}
        name={facilitiesInputName}
        onChange={onChangeFacilities}
        options={facilitiesAndServicesFormOptions}
        value={facilitiesValue}
      />
      <KeywordsSection
        errorMessages={fields.keyword.errors}
        headerClassName="md:w-full"
        layoutClassName="flex flex-col md:border-none"
        name={keywordInputName}
        onBlur={onBlurKeyword}
        onChange={onChangeKeyword}
        onEnterKeyDown={onEnterKeyword}
        value={keywordValue}
      />
      {children}
    </Form>
  )
}
